import { faClosedCaptioning } from '@fortawesome/pro-solid-svg-icons';
import { useLocalStorage } from 'usehooks-ts';

import { isIOS } from '@growtherapy/analytics';
import { useTranslate } from '@growtherapy/i18n';
import { CalloutBanner, Divider, InputControl, Link, LinkUse, Toggle, cn, useRebrand } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks';
import { Theme } from '../../state';
import { STORED_ACCESSIBILITY_SETTINGS_KEY } from './constants';

export type AccessibilitySettings = {
  disableNotificationTimeouts: boolean;
};

export type AccessibilitySettingsTabProps = {
  onChange: (settings: AccessibilitySettings) => void;
  accessibilitySettings?: AccessibilitySettings;
  showLiveCaptioningHelp: boolean;
};

const LIVE_CAPTIONING_HELP_ARTICLE =
  'https://help.growtherapy.com/en/articles/10365766-enable-live-captioning-for-appointments';

export function AccessibilitySettingsTab({
  onChange,
  accessibilitySettings,
  showLiveCaptioningHelp,
}: AccessibilitySettingsTabProps) {
  const t = useTranslate();

  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;
  const isRebrand = useRebrand();
  // The dark treatment is all `rebrand:` utilities, which are inert on the legacy brand.
  const isDarkBanner = isDarkTheme && isRebrand;

  return (
    <>
      <InputControl
        label={t('telehealth-core:settings.disableNotificationTimeouts')}
        labelClassName={cn('ml-2', {
          'rebrand:text-text-inverse': isDarkTheme,
        })}
        layoutClassName='flex flex-row-reverse items-center justify-end rebrand:flex-row-reverse rebrand:items-center rebrand:justify-end'
        className='mb-0'
      >
        <Toggle
          checked={accessibilitySettings?.disableNotificationTimeouts || false}
          onChange={(e) => {
            onChange({
              disableNotificationTimeouts: e.target.checked,
            });
          }}
        />
      </InputControl>
      <div
        className={cn('mt-4 text-sm text-neutral-700 rebrand:typography-label-small', {
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      >
        {t('telehealth-core:settings.keepAllNotificationsOnTheScreenUntilYouDismissThem')}
      </div>

      {showLiveCaptioningHelp && (
        <>
          {/**
           * `Divider` hardcodes `rebrand:border-border-subtle`, which is too
           * bright on the dark drawer surface.
           */}
          <Divider className='mt-4 mb-6 border-neutral-600 arc:border-border-default' />

          <CalloutBanner
            use={isDarkBanner ? 'black' : 'yellow'}
            type='alert'
            icon={faClosedCaptioning}
            heading={t('telehealth-core:settings.liveCaptionsAvailable')}
            headingAs='h3'
            className={cn({
              'bg-neutral-200': !isDarkBanner,
              // Same fill as the drawer, so a border separates it; `border-solid` undoes `border-none`.
              'rebrand:bg-surface-inverseEmphasis rebrand:border-solid rebrand:border-border-default': isDarkBanner,
            })}
          >
            {t('telehealth-core:settings.toViewCaptionsEnableLiveCaptionsInYourBrowser')}{' '}
            <Link
              href={LIVE_CAPTIONING_HELP_ARTICLE}
              use={isDarkBanner ? LinkUse.OnDark : LinkUse.Neutral}
              size='sm'
              external
            >
              {t('telehealth-core:settings.learnHowToEnableLiveCaptions')}
            </Link>
            <span className='sr-only'>{t('telehealth-core:settings.inYourBrowserOrDevicesAccessibilitySettings')}</span>
          </CalloutBanner>
        </>
      )}
    </>
  );
}

export function AccessibilitySettingsTabWrapper() {
  const [accessibilitySettings, setAccessibilitySettings] = useLocalStorage(STORED_ACCESSIBILITY_SETTINGS_KEY, {
    disableNotificationTimeouts: false,
  });

  return (
    <AccessibilitySettingsTab
      onChange={setAccessibilitySettings}
      accessibilitySettings={accessibilitySettings}
      showLiveCaptioningHelp={!isIOS(navigator.userAgent)}
    />
  );
}
